/**
 * **SHA-256 über einen Text, rein in TypeScript** — Auftrag `DR-vorlagenvertrag`
 * (Plan `docs/superpowers/plans/2026-09-01-datenregeln-umsetzung.md`, Task 13).
 *
 * ## Warum es diese Datei gibt
 *
 * `vorlagenHash()` (`datenregel-satz.ts`) muss in **drei** Umgebungen dasselbe
 * rechnen: im API-Prozess (Übernahme einer Vorlage, Import), im Test, der das
 * Fassungsbuch gegen den Katalog hält, und in der Oberfläche, die den Hash einer
 * Vorlage anzeigt und beim Export mitschickt. `packages/contracts` ist das Paket,
 * das alle drei lesen — und es hat weder `@types/node` noch die DOM-Typen
 * (`tsconfig.base.json`: `lib: ["ES2023"]`). `node:crypto` wäre im Browser-Bündel
 * eine Leerstelle, `crypto.subtle.digest()` ist asynchron und damit für eine
 * Funktion, die ein Fassungsbuch Zeile für Zeile prüft und an der Maske ohne
 * `await` gebraucht wird, die falsche Form. Eine Rechnung von rund vierzig Zeilen
 * ist der kleinere Preis als ein neues Paket (diese Ära baut ausdrücklich keins)
 * oder zwei verschiedene Hashwege.
 *
 * ## Was geprüft ist und was nicht
 *
 * Gegen `node:crypto` in `packages/contracts/test/datenregel-satz.test.ts`:
 * die drei Prüfvektoren aus FIPS 180-4, ein Text über mehrere Blöcke, die
 * Blockgrenzen (55, 56, 63, 64 Byte Eingabe) und Umlaute samt Zeichen ausserhalb
 * der Grundebene. **Nicht** gedacht ist sie für Geheimnisse: Sie läuft nicht in
 * konstanter Zeit, und sie braucht es nicht — sie hasht öffentliche Vorlagentexte.
 *
 * Diese Datei steht **nicht** im `index.ts`: Sie ist ein Werkzeug von
 * `datenregel-satz.ts`, kein Vertrag.
 */
/** Die 64 Rundenkonstanten (FIPS 180-4, 4.2.2). */
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
/** Die acht Anfangswerte (FIPS 180-4, 5.3.3). */
const H0 = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
];
function rechtsDrehen(wert, stellen) {
    return (wert >>> stellen) | (wert << (32 - stellen));
}
/**
 * Der Text als UTF-8-Bytes.
 *
 * Von Hand statt über `TextEncoder`, weil das Paket dessen Typ nicht kennt
 * (siehe oben). Ein einzelnes Ersatzzeichen ohne Gegenstück ergibt U+FFFD — wie
 * bei `TextEncoder`. Ein Text, der durch `JSON.stringify` gelaufen ist, enthält
 * keines (Ersatzzeichen werden dort maskiert), die kanonische Form der Vorlagen
 * kann es also gar nicht erst tragen.
 */
function utf8(text) {
    const bytes = [];
    for (let i = 0; i < text.length; i += 1) {
        let punkt = text.charCodeAt(i);
        if (punkt >= 0xd800 && punkt <= 0xdbff && i + 1 < text.length) {
            const folge = text.charCodeAt(i + 1);
            if (folge >= 0xdc00 && folge <= 0xdfff) {
                punkt = 0x10000 + ((punkt - 0xd800) << 10) + (folge - 0xdc00);
                i += 1;
            }
        }
        if (punkt >= 0xd800 && punkt <= 0xdfff)
            punkt = 0xfffd;
        if (punkt < 0x80) {
            bytes.push(punkt);
        }
        else if (punkt < 0x800) {
            bytes.push(0xc0 | (punkt >> 6), 0x80 | (punkt & 0x3f));
        }
        else if (punkt < 0x10000) {
            bytes.push(0xe0 | (punkt >> 12), 0x80 | ((punkt >> 6) & 0x3f), 0x80 | (punkt & 0x3f));
        }
        else {
            bytes.push(0xf0 | (punkt >> 18), 0x80 | ((punkt >> 12) & 0x3f), 0x80 | ((punkt >> 6) & 0x3f), 0x80 | (punkt & 0x3f));
        }
    }
    return Uint8Array.from(bytes);
}
/** SHA-256 des UTF-8-kodierten Textes als 64 kleine Hexziffern. */
export function sha256Hex(text) {
    const eingabe = utf8(text);
    const laenge = eingabe.length;
    // Eingabe, ein Byte 0x80, Nullen, dann die Länge in Bit als 64-Bit-Zahl.
    const gesamt = Math.ceil((laenge + 9) / 64) * 64;
    const puffer = new Uint8Array(gesamt);
    puffer.set(eingabe);
    puffer[laenge] = 0x80;
    const sicht = new DataView(puffer.buffer);
    sicht.setUint32(gesamt - 8, Math.floor(laenge / 0x20000000));
    sicht.setUint32(gesamt - 4, (laenge << 3) >>> 0);
    const h = Uint32Array.from(H0);
    const w = new Uint32Array(64);
    for (let block = 0; block < gesamt; block += 64) {
        for (let t = 0; t < 16; t += 1)
            w[t] = sicht.getUint32(block + t * 4);
        for (let t = 16; t < 64; t += 1) {
            const w15 = w[t - 15];
            const w2 = w[t - 2];
            const s0 = rechtsDrehen(w15, 7) ^ rechtsDrehen(w15, 18) ^ (w15 >>> 3);
            const s1 = rechtsDrehen(w2, 17) ^ rechtsDrehen(w2, 19) ^ (w2 >>> 10);
            w[t] = (w[t - 16] + s0 + w[t - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, i] = h;
        for (let t = 0; t < 64; t += 1) {
            const sigma1 = rechtsDrehen(e, 6) ^ rechtsDrehen(e, 11) ^ rechtsDrehen(e, 25);
            const wahl = (e & f) ^ (~e & g);
            const eins = (i + sigma1 + wahl + K[t] + w[t]) >>> 0;
            const sigma0 = rechtsDrehen(a, 2) ^ rechtsDrehen(a, 13) ^ rechtsDrehen(a, 22);
            const mehrheit = (a & b) ^ (a & c) ^ (b & c);
            const zwei = (sigma0 + mehrheit) >>> 0;
            i = g;
            g = f;
            f = e;
            e = (d + eins) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (eins + zwei) >>> 0;
        }
        const neu = [a, b, c, d, e, f, g, i];
        for (let t = 0; t < 8; t += 1)
            h[t] = (h[t] + neu[t]) >>> 0;
    }
    return Array.from(h, (wort) => wort.toString(16).padStart(8, '0')).join('');
}
//# sourceMappingURL=sha256.js.map